import React, { useEffect, useState } from "react";
import { RaffleContext } from "./context";
import { TabKey } from "../../redux/features/raffle/renders/christmas-raffle/AllPrizesModal/utils/types";
import { useAppDispatch } from "../../redux/hooks";
import { publishModalData, publishModalProps, publishModalVisibility } from "../../redux/features/modal/modalSlice";
import { AllPrizesModal } from "../../redux/features/raffle/renders/christmas-raffle/AllPrizesModal/AllPrizesModal";
import { useSmarticoRaffle } from "@/app/redux/features/raffle/store";
import { getUserSubscriptionPlan } from "@/app/(general-header)/subscription/subscription-service";

const RaffleContextProvider = ({ children }: Readonly<{ children: React.ReactNode }>) => {
  const [activeCompetitionsLoading, setActiveCompetitionsLoading] = useState(true);
  const [activeCompetitions, setActiveCompetitions] = useState<Array<any>>([]);
  const [showAllCompetitions, setShowAllCompetitions] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const raffle = useSmarticoRaffle();
  const isEndState = (raffle.end_date && new Date().getTime() > new Date(raffle.end_date).getTime()) || false;
  const dispatch = useAppDispatch();
  const viewAllPrizes = (tabKey: TabKey) => {
    dispatch(
      publishModalData({
        modalData: <AllPrizesModal defaultTab={tabKey} />,
      })
    );
    dispatch(
      publishModalProps({
        withOverlay: false,
      })
    );
    dispatch(
      publishModalVisibility({
        isOpen: true,
      })
    );
  };
  const onCloseModal = () => {
    dispatch(publishModalVisibility({ isOpen: false }));
  };

  const getActiveCompetitions = () => {
    return fetch(`${process.env.NEXT_PUBLIC_HOST}/umbraco/00000001/app/getactivecompetitions`);
  };

  const zeroPrices = ["0.00", "0,00"];

  const getReferencePriority = (reference: any) => {
    if (reference.includes("HC")) return 2;
    if (reference.includes("DC")) return 1;
    if (reference.includes("IW") || reference.includes("IA") || reference.includes("IB") || reference.includes("IC"))
      return 4;
    if (reference.includes("XC")) return 3;
    if (reference.includes("LS")) return 5;
    if (reference.includes("EW")) return 6;
    return 6;
  };

  const getHighestInstantWin = (data: any) => {
    const instantWins = data.filter(
      (competition: any) =>
        competition.Reference.includes("IW") ||
        competition.Reference.includes("IA") ||
        competition.Reference.includes("IB") ||
        competition.Reference.includes("IC")
    );
    if (instantWins.length === 0) return data;
    instantWins.sort(
      (a: any, b: any) => parseFloat(b.FormattedPriceWithoutCurrency) - parseFloat(a.FormattedPriceWithoutCurrency)
    );
    const otherCompetitions = data.filter(
      (competition: any) =>
        !competition.Reference.includes("IW") &&
        !competition.Reference.includes("IA") &&
        !competition.Reference.includes("IB") &&
        !competition.Reference.includes("IC")
    );
    return [...otherCompetitions, ...instantWins];
  };

  const parseCompetitionData = (data: any) => {
    const hasPriceCompetitions = data.filter(
      (competition: any) => !zeroPrices.includes(competition.FormattedPriceWithoutCurrency)
    );
    return getHighestInstantWin(hasPriceCompetitions)
      .sort((a: any, b: any) => getReferencePriority(a.Reference) - getReferencePriority(b.Reference))
      .map((competition: any) => {
        if (competition.Reference.includes("DC")) {
          return {
            ...competition,
            ticketCount: 1,
            FormattedOriginalPrice: "4.10",
          };
        } else if (competition.Reference.includes("HC")) {
          return {
            ...competition,
            ticketCount: 40,
            FormattedOriginalPrice: competition.FormattedOriginalPrice.replace(competition.CurrencySymbol, ""),
          };
        } else if (competition.Reference.includes("LS")) {
          return {
            ...competition,
            ticketCount: 15,
            FormattedOriginalPrice: "2.85",
          };
        }
        return {
          ...competition,
          ticketCount: 15,
          FormattedOriginalPrice: competition.FormattedOriginalPrice.replace(competition.CurrencySymbol, ""),
        };
      });
  };

  const getFinalData = (data: any) => {
    return data?.length > 0 ? parseCompetitionData(data) : [];
  };

  useEffect(() => {
    const abortController = new AbortController();
    getActiveCompetitions()
      .then(res => res.json())
      .then(res => {
        setActiveCompetitionsLoading(false);
        const resCompetitions = getFinalData(res);
        const competitionsToDisplay = resCompetitions
          .map(item => ({
            ...item,
            Points: setPointsByCompetitionType(item),
          }))
          .sort((a, b) => b.Points - a.Points);
        setActiveCompetitions(competitionsToDisplay);
      })
      .catch(err => console.error(err));

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await getUserSubscriptionPlan();
        const subscription = (await response.json()) as SubscriptionPlanUser;
        setHasSubscription(typeof subscription !== "undefined");
      } catch (err) {
        setHasSubscription(false);
      }
    })();
  }, []);

  const setPointsByCompetitionType = (item: any) => {
    if (item.Reference.includes("DC")) return 10;
    if (item.Reference.includes("LS")) return 1;
    if (item.Reference.includes("XC")) return 1;
    if (item.Reference.includes("HC")) return 12;
    if (item.Reference.includes("IW")) return 6;
    if (item.Reference.includes("IA") || item.Reference.includes("IB") || item.Reference.includes("IC")) return 6;
    if (item.Reference.includes("EW")) return 1;
    return 1;
  };

  const onCompetitionClick = (item: any) => {
    location.href = item.PageUrl;
    return;
  };

  const seeAllCompetitionsClick = () => {
    viewAllPrizes(TabKey.PREMII_ZILNIC);
  };
  const howToPlayClick = () => {
    viewAllPrizes(TabKey.CUM_PARTICIP);
  };

  return (
    <RaffleContext.Provider
      value={{
        viewAllPrizes,
        onCloseModal,
        howToPlayClick,
        onCompetitionClick,
        activeCompetitions,
        activeCompetitionsLoading,
        setPointsByCompetitionType,
        showAllCompetitions,
        seeAllCompetitionsClick,
        isEndState,
        hasSubscription,
      }}>
      {children}
    </RaffleContext.Provider>
  );
};

export default RaffleContextProvider;
